import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Trash2, Users, Clock, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMatchesPage = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    checkAdminAndFetch();
  }, []);

  const checkAdminAndFetch = async () => {
    try {
      const adminCheck = await axios.get(`${API_URL}/api/admin/check`, {
        withCredentials: true
      });
      
      if (!adminCheck.data.is_admin) {
        toast.error('Accesso non autorizzato');
        navigate('/dashboard');
        return;
      }
      
      fetchMatches();
    } catch (error) {
      toast.error('Accesso non autorizzato');
      navigate('/dashboard');
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/matches`, {
        withCredentials: true
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast.error('Errore nel caricamento partite');
    } finally {
      setLoading(false);
    }
  };

  const deleteMatch = async (matchId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/matches/${matchId}`, {
        withCredentials: true
      });
      setMatches(matches.filter(m => m.match_id !== matchId));
      toast.success('Partita eliminata');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const formatMatchDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEE d MMM yyyy', { locale: it });
    } catch {
      return dateStr;
    }
  };

  const filteredMatches = matches.filter(m => {
    if (filter === 'all') return true;
    return m.status === filter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-600',
      full: 'bg-sky-100 text-sky-600',
      completed: 'bg-slate-100 text-slate-600',
      cancelled: 'bg-red-100 text-red-600'
    };
    const labels = {
      open: 'Aperta',
      full: 'Completa',
      completed: 'Giocata',
      cancelled: 'Annullata'
    };
    return (
      <span className={`text-xs font-medium px-2 py-1 rounded-full ${styles[status] || styles.open}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-slate-400">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="admin-matches-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin')}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-sky-500" />
            Gestione Partite
          </h1>
          <p className="text-slate-500">{matches.length} partite totali</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { key: 'all', label: 'Tutte' },
          { key: 'open', label: 'Aperte' },
          { key: 'full', label: 'Complete' },
          { key: 'completed', label: 'Giocate' },
          { key: 'cancelled', label: 'Annullate' }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              filter === f.key
                ? 'bg-sky-400 text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-sky-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Matches List */}
      {filteredMatches.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nessuna partita trovata</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMatches.map((match) => (
            <div
              key={match.match_id}
              className="bg-white rounded-2xl p-4 border border-slate-100"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-sky-500" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-slate-900 capitalize">
                      {formatMatchDate(match.date)}
                    </p>
                    {getStatusBadge(match.status)}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {match.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {match.players?.length || 0}/{match.max_players}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Organizzata da {match.creator_name}
                  </p>
                </div>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-3xl">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        Eliminare questa partita?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Partita del {formatMatchDate(match.date)} alle {match.time}.
                        Questa azione non può essere annullata.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteMatch(match.match_id)}
                        className="rounded-full bg-red-500 hover:bg-red-600"
                      >
                        Elimina
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMatchesPage;
